import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';
import { useAuth } from '../AuthContext';

const PROFILE_FIELDS = [
  'full_name','gender','date_of_birth','height_cm','weight_kg','complexion','blood_group',
  'religion','caste','city','state','education','occupation','annual_income','about_me','profile_photo',
];

function calcCompleteness(profile) {
  const filled = PROFILE_FIELDS.filter(f => profile[f] && profile[f] !== '').length;
  return Math.round((filled / PROFILE_FIELDS.length) * 100);
}

function initials(name) {
  return (name || 'AB').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function Field({ label, value, icon }) {
  return (
    <div className="m-detail-field">
      <div className="m-detail-field-label">{icon} {label}</div>
      <div className="m-detail-field-value" style={{ color: value ? undefined : 'var(--m-muted-light)' }}>
        {value || '—'}
      </div>
    </div>
  );
}

const COMPLEXIONS   = ['Very Fair', 'Fair', 'Wheatish', 'Wheatish Brown', 'Dark'];
const BLOOD_GROUPS  = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const RELIGIONS     = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const EDUCATIONS    = ['High School', 'Diploma', 'Bachelor\'s', 'Master\'s', 'PhD', 'Professional Degree', 'Other'];
const INCOMES       = ['Below 2 LPA', '2-5 LPA', '5-10 LPA', '10-20 LPA', '20-50 LPA', '50+ LPA'];
const FAMILY_TYPES  = ['Nuclear', 'Joint', 'Extended'];
const FAMILY_VALUES = ['Traditional', 'Moderate', 'Liberal'];
const DIETS         = ['Vegetarian', 'Non-Vegetarian', 'Vegan', 'Eggetarian'];
const YES_NO        = ['Yes', 'No'];

export default function MatrimonyDashboard() {
  const { user, profile: authProfile, refresh } = useAuth();
  const [activeTab, setActiveTab] = useState('view');
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [pwd, setPwd]     = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdErr, setPwdErr]   = useState('');
  const [pwdOk, setPwdOk]     = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview]     = useState('');
  const [horoUploading, setHoroUploading]   = useState(false);
  const [horoErr, setHoroErr]               = useState('');
  const [zoomPhoto, setZoomPhoto]           = useState(false);
  const photoInputRef = useRef(null);
  const horoInputRef  = useRef(null);

  const handleZoomKey = useCallback((e) => { if (e.key === 'Escape') setZoomPhoto(false); }, []);
  useEffect(() => {
    if (zoomPhoto) document.addEventListener('keydown', handleZoomKey);
    else document.removeEventListener('keydown', handleZoomKey);
    return () => document.removeEventListener('keydown', handleZoomKey);
  }, [zoomPhoto, handleZoomKey]);

  useEffect(() => {
    if (authProfile) {
      setForm({ ...authProfile });
    }
  }, [authProfile]);

  if (user === undefined) {
    return (
      <div className="m-container">
        <div className="m-loading"><div className="m-spinner" /><span>Loading…</span></div>
      </div>
    );
  }

  if (user === null) return <Navigate to="/login" replace />;

  const p = authProfile || {};
  const completeness = calcCompleteness(p);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoUploading(true);
    setSaveErr('');
    try {
      const res = await matrimonyApi.uploadPhoto(file);
      set('profile_photo', res.url);
    } catch (err) {
      setSaveErr(err.message || 'Photo upload failed');
      setPhotoPreview('');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleHoroChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHoroUploading(true);
    setHoroErr('');
    try {
      const res = await matrimonyApi.uploadHoroscope(file);
      set('horoscope_url', res.url);
      await refresh();
    } catch (err) {
      setHoroErr(err.message || 'Horoscope upload failed');
    } finally {
      setHoroUploading(false);
    }
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    setPwdErr(''); setPwdOk(false);
    if (!pwd.currentPassword) { setPwdErr('Enter your current password'); return; }
    if (pwd.newPassword.length < 6) { setPwdErr('New password must be at least 6 characters'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { setPwdErr('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await matrimonyApi.changePassword({ currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      setPwdOk(true);
      setPwd({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwdErr(err.message || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveMsg(''); setSaveErr('');
    try {
      await matrimonyApi.updateProfile(form);
      await refresh();
      setSaveMsg('Profile updated successfully! ✓');
      setActiveTab('view');
    } catch (err) {
      setSaveErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  const statusClass = user.status === 'approved' ? 'm-status-approved'
    : user.status === 'rejected' ? 'm-status-rejected'
    : 'm-status-pending';

  const statusMsg = user.status === 'approved'
    ? '✅ Your account is approved! Your profile is visible to other members.'
    : user.status === 'rejected'
    ? '❌ Your account was not approved. Please contact support.'
    : '⏳ Your account is pending admin approval. It will be visible after approval.';

  return (
    <div className="m-container">
      {/* Full-photo lightbox */}
      {zoomPhoto && p.profile_photo && (
        <div className="m-photo-overlay" onClick={() => setZoomPhoto(false)}>
          <img src={p.profile_photo} alt={user.name} className="m-photo-overlay-img" />
          <button className="m-photo-overlay-close" onClick={() => setZoomPhoto(false)}>✕</button>
        </div>
      )}

      {/* Status banner */}
      <div className={`m-status-banner ${statusClass}`}>{statusMsg}</div>

      {/* Profile header */}
      <div className="m-detail-header" style={{ marginBottom: 24 }}>
        <div
          className={`m-detail-avatar${p.profile_photo ? ' m-avatar-zoomable' : ''}`}
          onClick={() => p.profile_photo && setZoomPhoto(true)}
          title={p.profile_photo ? 'Click to view full photo' : undefined}
        >
          {p.profile_photo
            ? <img src={p.profile_photo} alt={user.name} />
            : initials(user.name)}
        </div>
        <div className="m-detail-info">
          <h1 className="m-detail-name">{user.name}</h1>
          <p style={{ color: 'var(--m-muted)', fontSize: 14 }}>{user.email}</p>
          {p.city && <p style={{ color: 'var(--m-muted)', fontSize: 14 }}>📍 {p.city}{p.state ? `, ${p.state}` : ''}</p>}
          {/* Completeness */}
          <div style={{ marginTop: 12, maxWidth: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--m-muted)', marginBottom: 4 }}>
              <span>Profile completeness</span>
              <span><strong>{completeness}%</strong></span>
            </div>
            <div className="m-progress-bar">
              <div className="m-progress-fill" style={{ width: `${completeness}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="m-tabs">
        <button className={`m-tab${activeTab === 'view' ? ' active' : ''}`} onClick={() => setActiveTab('view')}>
          👤 My Profile
        </button>
        <button className={`m-tab${activeTab === 'edit' ? ' active' : ''}`} onClick={() => setActiveTab('edit')}>
          ✏️ Edit Profile
        </button>
        <button className={`m-tab${activeTab === 'security' ? ' active' : ''}`} onClick={() => { setActiveTab('security'); setPwdErr(''); setPwdOk(false); }}>
          🔒 Security
        </button>
      </div>

      {/* View tab */}
      {activeTab === 'view' && (
        <div>
          {/* Personal info */}
          <div className="m-detail-section">
            <div className="m-detail-section-title">👤 Personal Information</div>
            <div className="m-detail-grid">
              <Field label="Full Name"    value={p.full_name}    icon="👤" />
              <Field label="Gender"       value={p.gender}       icon="⚧️" />
              <Field label="Age"          value={p.age ? `${p.age} years` : null} icon="🎂" />
              <Field label="Height"       value={p.height_cm ? `${p.height_cm} cm` : null} icon="📏" />
              <Field label="Weight"       value={p.weight_kg ? `${p.weight_kg} kg` : null} icon="⚖️" />
              <Field label="Complexion"   value={p.complexion}   icon="🌟" />
              <Field label="Blood Group"  value={p.blood_group}  icon="🩸" />
              <Field label="Mother Tongue" value={p.mother_tongue} icon="🗣️" />
              <Field label="City"         value={p.city}         icon="📍" />
              <Field label="State"        value={p.state}        icon="🗺️" />
              <Field label="Diet"         value={p.diet}         icon="🍽️" />
              <Field label="Smoking"      value={p.smoking}      icon="🚬" />
              <Field label="Drinking"     value={p.drinking}     icon="🍷" />
            </div>
            {p.hobbies && (
              <div style={{ marginTop: 16 }}>
                <div className="m-detail-field-label">🎨 HOBBIES</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {p.hobbies.split(',').map(h => h.trim()).filter(Boolean).map((h, i) => (
                    <span key={i} className="m-tag">{h}</span>
                  ))}
                </div>
              </div>
            )}
            {p.about_me && (
              <div style={{ marginTop: 16 }}>
                <div className="m-detail-field-label">📝 ABOUT ME</div>
                <p style={{ marginTop: 6, fontSize: 14, lineHeight: 1.7, color: 'var(--m-text)' }}>{p.about_me}</p>
              </div>
            )}
            {p.horoscope_url && (
              <div style={{ marginTop: 16 }}>
                <a
                  href={p.horoscope_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m-btn m-btn-outline m-btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  🔯 View Horoscope
                </a>
              </div>
            )}
          </div>

          {/* Religion */}
          <div className="m-detail-section">
            <div className="m-detail-section-title">🙏 Religion &amp; Community</div>
            <div className="m-detail-grid">
              <Field label="Religion"  value={p.religion}  icon="🕌" />
              <Field label="Caste"     value={p.caste}     icon="🏷️" />
              <Field label="Sub Caste" value={p.sub_caste} icon="🏷️" />
            </div>
          </div>

          {/* Family */}
          <div className="m-detail-section">
            <div className="m-detail-section-title">👨‍👩‍👧‍👦 Family</div>
            <div className="m-detail-grid">
              <Field label="Family Type"          value={p.family_type}       icon="🏠" />
              <Field label="Family Values"        value={p.family_values}     icon="💝" />
              <Field label="Father's Occupation"  value={p.father_occupation} icon="👔" />
              <Field label="Mother's Occupation"  value={p.mother_occupation} icon="👩‍💼" />
              <Field label="No. of Siblings"      value={p.siblings != null ? String(p.siblings) : null} icon="👥" />
            </div>
          </div>

          {/* Career */}
          <div className="m-detail-section">
            <div className="m-detail-section-title">💼 Education &amp; Career</div>
            <div className="m-detail-grid">
              <Field label="Education"     value={p.education}       icon="🎓" />
              <Field label="Field"         value={p.education_field} icon="📖" />
              <Field label="Occupation"    value={p.occupation}      icon="💼" />
              <Field label="Employer"      value={p.employer}        icon="🏢" />
              <Field label="Annual Income" value={p.annual_income}   icon="💰" />
            </div>
          </div>

          {/* Preferences */}
          <div className="m-detail-section">
            <div className="m-detail-section-title">💍 Partner Preferences</div>
            <div className="m-detail-grid">
              {(p.pref_age_min || p.pref_age_max) && (
                <Field label="Age Range" value={`${p.pref_age_min || '?'} – ${p.pref_age_max || '?'} years`} icon="🎂" />
              )}
              <Field label="Preferred Religion"  value={p.pref_religion}  icon="🙏" />
              <Field label="Preferred Caste"     value={p.pref_caste}     icon="🏷️" />
              <Field label="Preferred Education" value={p.pref_education} icon="🎓" />
              <Field label="Preferred Income"    value={p.pref_income}    icon="💰" />
              <Field label="Preferred Location"  value={p.pref_location}  icon="📍" />
            </div>
          </div>
        </div>
      )}

      {/* Edit tab */}
      {activeTab === 'edit' && (
        <form onSubmit={handleSave}>
          {saveMsg && <div className="m-alert m-alert-success">{saveMsg}</div>}
          {saveErr && <div className="m-alert m-alert-error">{saveErr}</div>}

          {/* Personal info */}
          <div className="m-card" style={{ marginBottom: 20 }}>
            <div className="m-card-header"><div className="m-card-title">👤 Personal Information</div></div>
            <div className="m-card-body">
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Full Name</label>
                  <input className="m-form-control" value={form.full_name || ''} onChange={e => set('full_name', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Gender</label>
                  <select className="m-form-control" value={form.gender || ''} onChange={e => set('gender', e.target.value)}>
                    <option value="">Select</option>
                    <option value="bride">👰 Bride</option>
                    <option value="groom">🤵 Groom</option>
                  </select>
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Date of Birth</label>
                  <input className="m-form-control" type="date" value={form.date_of_birth ? String(form.date_of_birth).split('T')[0] : ''} onChange={e => set('date_of_birth', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Blood Group</label>
                  <select className="m-form-control" value={form.blood_group || ''} onChange={e => set('blood_group', e.target.value)}>
                    <option value="">Select</option>
                    {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Height (cm)</label>
                  <input className="m-form-control" type="number" value={form.height_cm || ''} onChange={e => set('height_cm', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Weight (kg)</label>
                  <input className="m-form-control" type="number" value={form.weight_kg || ''} onChange={e => set('weight_kg', e.target.value)} />
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Complexion</label>
                  <select className="m-form-control" value={form.complexion || ''} onChange={e => set('complexion', e.target.value)}>
                    <option value="">Select</option>
                    {COMPLEXIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Mother Tongue</label>
                  <input className="m-form-control" value={form.mother_tongue || ''} onChange={e => set('mother_tongue', e.target.value)} />
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">City</label>
                  <input className="m-form-control" value={form.city || ''} onChange={e => set('city', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">State</label>
                  <input className="m-form-control" value={form.state || ''} onChange={e => set('state', e.target.value)} />
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Diet</label>
                  <select className="m-form-control" value={form.diet || ''} onChange={e => set('diet', e.target.value)}>
                    <option value="">Select</option>
                    {DIETS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Smoking</label>
                  <select className="m-form-control" value={form.smoking || 'No'} onChange={e => set('smoking', e.target.value)}>
                    {YES_NO.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Drinking</label>
                <select className="m-form-control" value={form.drinking || 'No'} onChange={e => set('drinking', e.target.value)}>
                  {YES_NO.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Hobbies (comma-separated)</label>
                <input className="m-form-control" placeholder="Reading, Cooking, Travelling…" value={form.hobbies || ''} onChange={e => set('hobbies', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">About Me</label>
                <textarea className="m-form-control" rows={4} value={form.about_me || ''} onChange={e => set('about_me', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Profile Photo</label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
                <div className="m-upload-row" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {(photoPreview || form.profile_photo) && (
                    <img
                      src={photoPreview || form.profile_photo}
                      alt="Profile"
                      style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--m-primary)' }}
                    />
                  )}
                  <button
                    type="button"
                    className="m-btn m-btn-outline m-btn-sm"
                    onClick={() => photoInputRef.current && photoInputRef.current.click()}
                    disabled={photoUploading}
                    style={{ minWidth: 150 }}
                  >
                    {photoUploading ? '⏳ Uploading…' : form.profile_photo ? '🔄 Change Photo' : '📷 Upload Photo'}
                  </button>
                </div>
                {form.profile_photo && !photoUploading && (
                  <div className="m-form-hint" style={{ color: 'var(--m-success, #2e7d32)', marginTop: 6 }}>
                    ✓ Photo saved
                  </div>
                )}
                <div className="m-form-hint" style={{ marginTop: 4 }}>Max 5 MB · JPG, PNG, WebP accepted</div>
              </div>

              <div className="m-form-group">
                <label className="m-form-label">Horoscope Document</label>
                <input
                  ref={horoInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  style={{ display: 'none' }}
                  onChange={handleHoroChange}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="m-btn m-btn-outline m-btn-sm"
                    onClick={() => horoInputRef.current && horoInputRef.current.click()}
                    disabled={horoUploading}
                    style={{ minWidth: 180 }}
                  >
                    {horoUploading ? '⏳ Uploading…' : form.horoscope_url ? '🔄 Replace Horoscope' : '🔯 Upload Horoscope'}
                  </button>
                  {form.horoscope_url && !horoUploading && (
                    <a
                      href={form.horoscope_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="m-btn m-btn-ghost m-btn-sm"
                    >
                      🔯 View Current
                    </a>
                  )}
                </div>
                {horoErr && <div className="m-form-hint" style={{ color: 'var(--m-error)', marginTop: 4 }}>{horoErr}</div>}
                {form.horoscope_url && !horoUploading && (
                  <div className="m-form-hint" style={{ color: 'var(--m-success, #2e7d32)', marginTop: 4 }}>✓ Horoscope uploaded</div>
                )}
                <div className="m-form-hint" style={{ marginTop: 4 }}>PDF, DOC, DOCX or image · Max 10 MB</div>
              </div>
            </div>
          </div>

          {/* Religion */}
          <div className="m-card" style={{ marginBottom: 20 }}>
            <div className="m-card-header"><div className="m-card-title">🙏 Religion &amp; Community</div></div>
            <div className="m-card-body">
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Religion</label>
                  <select className="m-form-control" value={form.religion || ''} onChange={e => set('religion', e.target.value)}>
                    <option value="">Select</option>
                    {RELIGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Caste</label>
                  <input className="m-form-control" value={form.caste || ''} onChange={e => set('caste', e.target.value)} />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Sub Caste</label>
                <input className="m-form-control" value={form.sub_caste || ''} onChange={e => set('sub_caste', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Family */}
          <div className="m-card" style={{ marginBottom: 20 }}>
            <div className="m-card-header"><div className="m-card-title">👨‍👩‍👧‍👦 Family</div></div>
            <div className="m-card-body">
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Family Type</label>
                  <select className="m-form-control" value={form.family_type || ''} onChange={e => set('family_type', e.target.value)}>
                    <option value="">Select</option>
                    {FAMILY_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Family Values</label>
                  <select className="m-form-control" value={form.family_values || ''} onChange={e => set('family_values', e.target.value)}>
                    <option value="">Select</option>
                    {FAMILY_VALUES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Father's Occupation</label>
                  <input className="m-form-control" value={form.father_occupation || ''} onChange={e => set('father_occupation', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Mother's Occupation</label>
                  <input className="m-form-control" value={form.mother_occupation || ''} onChange={e => set('mother_occupation', e.target.value)} />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Number of Siblings</label>
                <input className="m-form-control" type="number" min="0" max="20" style={{ maxWidth: 120 }} value={form.siblings || 0} onChange={e => set('siblings', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Career */}
          <div className="m-card" style={{ marginBottom: 20 }}>
            <div className="m-card-header"><div className="m-card-title">💼 Education &amp; Career</div></div>
            <div className="m-card-body">
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Education</label>
                  <select className="m-form-control" value={form.education || ''} onChange={e => set('education', e.target.value)}>
                    <option value="">Select</option>
                    {EDUCATIONS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Field of Study</label>
                  <input className="m-form-control" value={form.education_field || ''} onChange={e => set('education_field', e.target.value)} />
                </div>
              </div>
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Occupation</label>
                  <input className="m-form-control" value={form.occupation || ''} onChange={e => set('occupation', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Employer</label>
                  <input className="m-form-control" value={form.employer || ''} onChange={e => set('employer', e.target.value)} />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Annual Income</label>
                <select className="m-form-control" value={form.annual_income || ''} onChange={e => set('annual_income', e.target.value)}>
                  <option value="">Select</option>
                  {INCOMES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="m-card" style={{ marginBottom: 20 }}>
            <div className="m-card-header"><div className="m-card-title">💍 Partner Preferences</div></div>
            <div className="m-card-body">
              <div className="m-form-row">
                <div className="m-form-group">
                  <label className="m-form-label">Partner Age Min</label>
                  <input className="m-form-control" type="number" min="18" max="70" value={form.pref_age_min || ''} onChange={e => set('pref_age_min', e.target.value)} />
                </div>
                <div className="m-form-group">
                  <label className="m-form-label">Partner Age Max</label>
                  <input className="m-form-control" type="number" min="18" max="70" value={form.pref_age_max || ''} onChange={e => set('pref_age_max', e.target.value)} />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Preferred Religion</label>
                <input className="m-form-control" placeholder="e.g. Hindu, Any" value={form.pref_religion || ''} onChange={e => set('pref_religion', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Preferred Caste</label>
                <input className="m-form-control" placeholder="e.g. Brahmin, Any" value={form.pref_caste || ''} onChange={e => set('pref_caste', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Preferred Education</label>
                <input className="m-form-control" placeholder="e.g. Bachelor's or above" value={form.pref_education || ''} onChange={e => set('pref_education', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Preferred Income</label>
                <input className="m-form-control" placeholder="e.g. 5 LPA+" value={form.pref_income || ''} onChange={e => set('pref_income', e.target.value)} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Preferred Location</label>
                <input className="m-form-control" placeholder="e.g. Hyderabad, Bangalore, Any" value={form.pref_location || ''} onChange={e => set('pref_location', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="m-form-actions" style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Profile'}
            </button>
            <button type="button" className="m-btn m-btn-ghost" onClick={() => setActiveTab('view')}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Security tab */}
      {activeTab === 'security' && (
        <div className="m-card" style={{ maxWidth: 480 }}>
          <div className="m-card-header"><div className="m-card-title">🔒 Change Password</div></div>
          <div className="m-card-body">
            {pwdErr && <div className="m-alert m-alert-error" style={{ marginBottom: 14 }}>{pwdErr}</div>}
            {pwdOk  && <div className="m-alert m-alert-success" style={{ marginBottom: 14 }}>Password changed successfully! ✓</div>}
            <form onSubmit={handleChangePwd}>
              <div className="m-form-group">
                <label className="m-form-label">Current Password</label>
                <input className="m-form-control" type="password" placeholder="Your current password"
                  value={pwd.currentPassword}
                  onChange={e => { setPwd(p => ({ ...p, currentPassword: e.target.value })); setPwdErr(''); setPwdOk(false); }} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">New Password <span style={{ fontWeight: 400, color: 'var(--m-muted)', fontSize: 12 }}>(min 6 characters)</span></label>
                <input className="m-form-control" type="password" placeholder="New password"
                  value={pwd.newPassword}
                  onChange={e => { setPwd(p => ({ ...p, newPassword: e.target.value })); setPwdErr(''); setPwdOk(false); }} />
              </div>
              <div className="m-form-group">
                <label className="m-form-label">Confirm New Password</label>
                <input className="m-form-control" type="password" placeholder="Repeat new password"
                  value={pwd.confirmPassword}
                  onChange={e => { setPwd(p => ({ ...p, confirmPassword: e.target.value })); setPwdErr(''); setPwdOk(false); }} />
              </div>
              <button type="submit" className="m-btn m-btn-primary" disabled={pwdSaving} style={{ marginTop: 4 }}>
                {pwdSaving ? 'Saving…' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
